import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, Newspaper, Building2, Trophy, Users, Calendar,
  ExternalLink, Mail, ChevronRight, Award, Mic, TrendingUp,
  FileText, Shield, Briefcase, Star, Globe
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { team } from '../data/mock';

const PressPage = () => {
  const [activeFilter, setActiveFilter] = useState('all');

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 25 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  };

  // Press releases data
  const pressReleases = [
    {
      id: 1,
      date: 'May 2025',
      title: 'ASAP Capital Acquires Multifamily Community in Greater Houston',
      summary: 'ASAP Capital announces the acquisition of a value-add multifamily property as part of its Texas-focused investment strategy.',
      category: 'press'
    },
    {
      id: 2,
      date: 'February 2025',
      title: 'ASAP Capital Completes Renovation Program at Houston Apartment Community',
      summary: 'Capital improvements focused on unit upgrades, amenity enhancements, and operational efficiencies.',
      category: 'press'
    },
    {
      id: 3,
      date: 'October 2024',
      title: 'ASAP Capital Expands Portfolio With Neighborhood Retail Acquisition',
      summary: 'Strategic expansion into stabilized retail assets in high-growth Houston submarkets.',
      category: 'press'
    },
    {
      id: 4,
      date: 'July 2024',
      title: 'ASAP Capital Announces New Investment Fund Targeting Texas Markets',
      summary: 'Launch of value-add real estate fund focused on Greater Houston metropolitan area.',
      category: 'press'
    }
  ];

  // Media mentions data
  const mediaMentions = [
    {
      id: 1,
      publication: 'Houston Business Journal',
      title: 'Local Investment Firm Targets Workforce Housing in High-Growth Submarkets',
      description: 'ASAP Capital discusses its value-add approach and Houston market outlook.',
      external: true,
      category: 'media'
    },
    {
      id: 2,
      publication: 'Texas Real Estate Journal',
      title: 'Private Equity Group Expands Multifamily Footprint Across Texas',
      description: 'Coverage of recent acquisitions and renovation programs.',
      external: true,
      category: 'media'
    },
    {
      id: 3,
      publication: 'Commercial Property Executive',
      title: 'Value-Add Strategy Drives Growth for Houston-Based Sponsor',
      description: 'Interview with leadership on market positioning and operational execution.',
      external: true,
      category: 'media'
    }
  ];

  // Awards data
  const awards = [
    {
      id: 1,
      title: 'Regional Real Estate Leadership',
      description: 'Recognized for operational execution and portfolio growth in Texas markets.',
      icon: Trophy
    },
    {
      id: 2,
      title: 'Multifamily Investment Excellence',
      description: 'Acknowledged for value-add renovation strategy and resident experience improvements.',
      icon: Award
    },
    {
      id: 3,
      title: 'Industry Association Memberships',
      description: 'Active participation in regional and national real estate organizations.',
      icon: Globe
    }
  ];

  // Recent announcements
  const announcements = [
    { year: '2025', title: 'Portfolio Expansion', description: 'New multifamily acquisitions added in targeted Houston submarkets.' },
    { year: '2024', title: 'Renovation Programs Completed', description: 'Capital improvement programs delivered across multiple properties.' },
    { year: '2024', title: 'Retail Asset Acquisition', description: 'Expansion into neighborhood retail to diversify income streams.' },
    { year: '2023', title: 'Team Expansion', description: 'Added experienced professionals to investment and asset management teams.' }
  ];

  // Leadership appearances
  const leadershipAppearances = [
    'Panel discussion on Houston multifamily outlook',
    'Interview on value-add repositioning strategies',
    'Speaker at regional commercial real estate conference',
    'Podcast feature on Texas investment opportunities'
  ];

  // Micro proof items
  const microProofs = [
    { icon: Building2, label: 'Portfolio Growth' },
    { icon: Trophy, label: 'Industry Recognition' },
    { icon: Newspaper, label: 'Media Coverage' }
  ];

  // Filter options
  const filterOptions = [
    { id: 'all', label: 'All' },
    { id: 'press', label: 'Press Releases' },
    { id: 'media', label: 'Media Mentions' },
    { id: 'awards', label: 'Awards' }
  ];

  return (
    <>
      <Helmet>
        <title>Press Releases and Media Coverage | ASAP Capital</title>
        <meta 
          name="description" 
          content="Updates, announcements, and media coverage highlighting ASAP Capital's growth, portfolio activity, and leadership in Houston and Texas real estate markets." 
        />
        <meta name="keywords" content="ASAP Capital press, Houston real estate news, multifamily investment announcements, Texas real estate media" />
        <link rel="canonical" href="/press" />
      </Helmet>

      <div className="min-h-screen bg-[#0A1628]">
        <Header />
        
        <main>
          {/* ==================== HERO SECTION ==================== */}
          <section className="relative pt-36 pb-16 lg:pt-44 lg:pb-20 overflow-hidden">
            {/* Background elements */}
            <div className="absolute inset-0 bg-gradient-to-b from-[#0A1628] via-[#0d1a2d] to-[#0A1628]" />
            <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#D4AF37]/5 rounded-full blur-[150px] pointer-events-none" />
            <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-blue-500/5 rounded-full blur-[120px] pointer-events-none" />
            
            <div className="relative z-10 max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                animate="visible"
                variants={containerVariants}
                className="text-center"
              >
                <motion.div 
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6 bg-[#D4AF37]/10 border border-[#D4AF37]/20"
                  variants={itemVariants}
                >
                  <Newspaper className="w-4 h-4 text-[#D4AF37]" />
                  <span className="text-xs font-semibold text-[#D4AF37] uppercase tracking-wider">Press & Media</span>
                </motion.div>

                <motion.h1 
                  className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-[1.2] mb-6"
                  variants={itemVariants}
                >
                  Press Releases and{' '}
                  <span className="bg-gradient-to-r from-[#D4AF37] via-[#F5D76E] to-[#D4AF37] bg-clip-text text-transparent">
                    Media Coverage
                  </span>
                </motion.h1>

                <motion.p 
                  className="text-white/60 text-base lg:text-lg leading-relaxed max-w-2xl mx-auto mb-10"
                  variants={itemVariants}
                >
                  Updates, announcements, and third-party media coverage highlighting ASAP Capital's 
                  growth, portfolio activity, and leadership in Houston and Texas real estate markets.
                </motion.p>

                {/* Micro Proof Bar */}
                <motion.div 
                  className="flex flex-wrap justify-center gap-6"
                  variants={itemVariants}
                >
                  {microProofs.map((proof, index) => (
                    <div 
                      key={proof.label}
                      className="flex items-center gap-2 text-white/50"
                    >
                      <proof.icon className="h-4 w-4 text-[#D4AF37]" />
                      <span className="text-sm">{proof.label}</span>
                    </div>
                  ))}
                </motion.div>
              </motion.div>
            </div>
          </section>

          {/* ==================== FILTER TABS ==================== */}
          <section className="py-6 relative bg-gradient-to-b from-[#0A1628] to-[#0d1a2d] border-b border-white/5">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <div className="flex flex-wrap justify-center gap-3">
                {filterOptions.map((option) => (
                  <button
                    key={option.id}
                    onClick={() => setActiveFilter(option.id)}
                    className={`px-5 py-2 rounded-full text-sm transition-all ${
                      activeFilter === option.id
                        ? 'bg-[#D4AF37] text-[#0A1628] font-semibold'
                        : 'bg-white/5 text-white/60 hover:bg-white/10 border border-white/10'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          </section>

          {/* ==================== COMPANY PRESS RELEASES ==================== */}
          {(activeFilter === 'all' || activeFilter === 'press') && (
            <section className="py-16 lg:py-20 relative">
              <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
                <motion.div
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, margin: "-100px" }}
                  variants={containerVariants}
                >
                  <motion.div className="flex items-center gap-3 mb-8" variants={itemVariants}>
                    <div className="w-10 h-10 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center border border-[#D4AF37]/20">
                      <FileText className="h-5 w-5 text-[#D4AF37]" />
                    </div>
                    <h2 className="text-2xl lg:text-3xl font-bold text-white">
                      Company Press Releases
                    </h2>
                  </motion.div>

                  <div className="grid md:grid-cols-2 gap-6">
                    {pressReleases.map((release) => (
                      <motion.div
                        key={release.id}
                        className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-2xl p-6 border border-white/10 hover:border-[#D4AF37]/30 transition-all duration-300 group cursor-pointer"
                        variants={itemVariants}
                        whileHover={{ y: -5 }}
                      >
                        <div className="flex items-center gap-2 text-[#D4AF37] text-sm mb-3">
                          <Calendar className="h-4 w-4" />
                          {release.date}
                        </div>
                        <h3 className="text-lg font-bold text-white mb-3 group-hover:text-[#D4AF37] transition-colors">
                          {release.title}
                        </h3>
                        <p className="text-white/50 text-sm mb-4 line-clamp-2">
                          {release.summary}
                        </p>
                        <span className="inline-flex items-center gap-2 text-[#D4AF37] text-sm font-medium group-hover:gap-3 transition-all">
                          Read Full Release
                          <ArrowRight className="h-4 w-4" />
                        </span>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              </div>
            </section>
          )}

          {/* ==================== MEDIA MENTIONS ==================== */}
          {(activeFilter === 'all' || activeFilter === 'media') && (
            <section className="py-16 lg:py-20 relative bg-gradient-to-b from-[#0d1a2d] to-[#0A1628]">
              <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
                <motion.div
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, margin: "-100px" }}
                  variants={containerVariants}
                >
                  <motion.div className="flex items-center gap-3 mb-8" variants={itemVariants}>
                    <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center border border-blue-500/20">
                      <Globe className="h-5 w-5 text-blue-400" />
                    </div>
                    <h2 className="text-2xl lg:text-3xl font-bold text-white">
                      Media Mentions & Features
                    </h2>
                  </motion.div>

                  <div className="space-y-4">
                    {mediaMentions.map((mention) => (
                      <motion.div
                        key={mention.id}
                        className="bg-gradient-to-r from-[#111D35] to-[#0A1628] rounded-xl p-6 border border-white/10 hover:border-blue-500/30 transition-all duration-300 group cursor-pointer"
                        variants={itemVariants}
                        whileHover={{ x: 5 }}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <p className="text-blue-400 font-semibold text-sm mb-2">{mention.publication}</p>
                            <h3 className="text-lg font-bold text-white mb-2 group-hover:text-blue-400 transition-colors">
                              {mention.title}
                            </h3>
                            <p className="text-white/50 text-sm">{mention.description}</p>
                          </div>
                          <div className="flex-shrink-0">
                            <span className="inline-flex items-center gap-2 text-blue-400 text-sm font-medium">
                              View Article
                              <ExternalLink className="h-4 w-4" />
                            </span>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              </div>
            </section>
          )}

          {/* ==================== AWARDS & RECOGNITION ==================== */}
          {(activeFilter === 'all' || activeFilter === 'awards') && (
            <section className="py-16 lg:py-20 relative">
              <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
                <motion.div
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, margin: "-100px" }}
                  variants={containerVariants}
                >
                  <motion.div className="flex items-center gap-3 mb-8" variants={itemVariants}>
                    <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center border border-amber-500/20">
                      <Trophy className="h-5 w-5 text-amber-400" />
                    </div>
                    <h2 className="text-2xl lg:text-3xl font-bold text-white">
                      Awards and Industry Recognition
                    </h2>
                  </motion.div>

                  <div className="grid md:grid-cols-3 gap-6">
                    {awards.map((award) => (
                      <motion.div
                        key={award.id}
                        className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-2xl p-6 border border-white/10 hover:border-amber-500/30 transition-all duration-300 text-center"
                        variants={itemVariants}
                        whileHover={{ y: -5, scale: 1.02 }}
                      >
                        <div className="w-16 h-16 rounded-2xl bg-amber-500/10 flex items-center justify-center border border-amber-500/20 mx-auto mb-4">
                          <award.icon className="h-8 w-8 text-amber-400" />
                        </div>
                        <h3 className="text-lg font-bold text-white mb-2">{award.title}</h3>
                        <p className="text-white/50 text-sm">{award.description}</p>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              </div>
            </section>
          )}

          {/* ==================== LEADERSHIP IN THE NEWS ==================== */}
          {activeFilter === 'all' && (
            <section className="py-16 lg:py-20 relative bg-gradient-to-b from-[#0A1628] to-[#0d1a2d]">
              <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
                <motion.div
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, margin: "-100px" }}
                  variants={containerVariants}
                  className="grid lg:grid-cols-2 gap-12 items-center"
                >
                  {/* Left - Photo */}
                  <motion.div 
                    className="relative"
                    variants={itemVariants}
                  >
                    <div className="relative rounded-2xl overflow-hidden">
                      <img 
                        src={team[0]?.image || "https://images.unsplash.com/photo-1560250097-0b93528c311a?w=600&q=80"}
                        alt="ASAP Capital Leadership"
                        className="w-full aspect-[4/5] object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#0A1628]/80 via-transparent to-transparent" />
                      <div className="absolute bottom-6 left-6">
                        <p className="text-white font-semibold">Leadership Team</p>
                        <p className="text-white/60 text-sm">ASAP Capital</p>
                      </div>
                    </div>
                  </motion.div>

                  {/* Right - Content */}
                  <motion.div variants={itemVariants}>
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-10 h-10 rounded-xl bg-purple-500/10 flex items-center justify-center border border-purple-500/20">
                        <Mic className="h-5 w-5 text-purple-400" />
                      </div>
                      <h2 className="text-2xl lg:text-3xl font-bold text-white">
                        Leadership in the News
                      </h2>
                    </div>

                    <p className="text-white/60 mb-6">
                      ASAP Capital leadership regularly contributes to industry discussions on 
                      market trends, investment strategy, and operational best practices.
                    </p>

                    <div className="mb-6">
                      <h4 className="text-white font-semibold mb-4">Recent Appearances & Interviews</h4>
                      <ul className="space-y-3">
                        {leadershipAppearances.map((appearance, index) => (
                          <li 
                            key={index}
                            className="flex items-start gap-3 text-white/50"
                          >
                            <ChevronRight className="h-5 w-5 text-purple-400 flex-shrink-0 mt-0.5" />
                            <span>{appearance}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <Link to="/team">
                      <Button className="bg-purple-500/10 hover:bg-purple-500/20 text-purple-400 border border-purple-500/30 rounded-full">
                        Meet Our Team
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </Link>
                  </motion.div>
                </motion.div>
              </div>
            </section>
          )}

          {/* ==================== RECENT ANNOUNCEMENTS ==================== */}
          {activeFilter === 'all' && (
            <section className="py-16 lg:py-20 relative">
              <div className="max-w-[900px] mx-auto px-6 lg:px-12">
                <motion.div
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, margin: "-100px" }}
                  variants={containerVariants}
                >
                  <motion.div className="flex items-center gap-3 mb-10" variants={itemVariants}>
                    <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center border border-emerald-500/20">
                      <TrendingUp className="h-5 w-5 text-emerald-400" />
                    </div>
                    <h2 className="text-2xl lg:text-3xl font-bold text-white">
                      Recent Announcements
                    </h2>
                  </motion.div>

                  {/* Timeline */}
                  <div className="relative">
                    {/* Timeline line */}
                    <div className="absolute left-4 top-0 bottom-0 w-px bg-gradient-to-b from-emerald-500/50 via-emerald-500/20 to-transparent" />

                    <div className="space-y-8">
                      {announcements.map((announcement, index) => (
                        <motion.div
                          key={index}
                          className="relative pl-12"
                          variants={itemVariants}
                        >
                          {/* Timeline dot */}
                          <div className="absolute left-0 top-1 w-8 h-8 rounded-full bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center">
                            <div className="w-3 h-3 rounded-full bg-emerald-400" />
                          </div>

                          <div className="bg-gradient-to-r from-[#111D35] to-transparent rounded-xl p-5 border-l-2 border-emerald-500/30">
                            <p className="text-emerald-400 font-bold text-sm mb-1">{announcement.year} — {announcement.title}</p>
                            <p className="text-white/50 text-sm">{announcement.description}</p>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                </motion.div>
              </div>
            </section>
          )}

          {/* ==================== MEDIA CONTACT ==================== */}
          <section className="py-16 lg:py-20 relative bg-gradient-to-b from-[#0d1a2d] to-[#0A1628]">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={containerVariants}
              >
                <motion.div className="flex items-center gap-3 mb-8" variants={itemVariants}>
                  <div className="w-10 h-10 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center border border-[#D4AF37]/20">
                    <Mail className="h-5 w-5 text-[#D4AF37]" />
                  </div>
                  <h2 className="text-2xl lg:text-3xl font-bold text-white">
                    Media Contact Information
                  </h2>
                </motion.div>

                <div className="grid md:grid-cols-2 gap-6">
                  <motion.div
                    className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-2xl p-6 border border-white/10"
                    variants={itemVariants}
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <Newspaper className="h-6 w-6 text-[#D4AF37]" />
                      <h3 className="text-lg font-bold text-white">Press & Media Inquiries</h3>
                    </div>
                    <a 
                      href="mailto:press@asapcapital.net"
                      className="text-[#D4AF37] hover:text-[#F5D76E] transition-colors"
                    >
                      press@asapcapital.net
                    </a>
                  </motion.div>

                  <motion.div
                    className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-2xl p-6 border border-white/10"
                    variants={itemVariants}
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <Users className="h-6 w-6 text-blue-400" />
                      <h3 className="text-lg font-bold text-white">Investor Relations</h3>
                    </div>
                    <a 
                      href="mailto:info@asapcapital.net"
                      className="text-blue-400 hover:text-blue-300 transition-colors"
                    >
                      info@asapcapital.net
                    </a>
                  </motion.div>
                </div>

                <motion.p 
                  className="text-white/40 text-sm mt-6"
                  variants={itemVariants}
                >
                  For investment-related inquiries, please refer to offering documents provided in official communications.
                </motion.p>
              </motion.div>
            </div>
          </section>

          {/* ==================== FINAL CTA ==================== */}
          <section className="py-20 lg:py-28 relative">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-3xl p-8 lg:p-12 border border-[#D4AF37]/20 relative overflow-hidden"
              >
                {/* Background accents */}
                <div className="absolute top-0 right-0 w-64 h-64 bg-[#D4AF37]/5 rounded-full blur-3xl" />
                <div className="absolute bottom-0 left-0 w-48 h-48 bg-blue-500/5 rounded-full blur-3xl" />
                
                <div className="relative z-10 text-center max-w-2xl mx-auto">
                  <h2 className="text-2xl lg:text-3xl font-bold text-white mb-4">
                    Learn More About Our Investment Platform
                  </h2>
                  <p className="text-white/50 mb-8">
                    Explore our portfolio, case studies, and investment approach to understand 
                    how we create value across Texas real estate markets.
                  </p>
                  
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <Link to="/portfolio">
                      <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                        <Button className="bg-gradient-to-r from-[#D4AF37] to-[#B8962E] text-[#0A1628] font-semibold px-8 py-3 h-12 rounded-full shadow-lg shadow-[#D4AF37]/20 w-full sm:w-auto">
                          View Portfolio
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                      </motion.div>
                    </Link>
                    <Link to="/contact">
                      <button className="cta-contact px-8 py-3 h-12 rounded-full w-full sm:w-auto">
                        Schedule Consultation
                        <Calendar className="h-4 w-4" />
                      </button>
                    </Link>
                  </div>
                </div>
              </motion.div>
            </div>
          </section>

          {/* ==================== COMPLIANCE DISCLAIMER ==================== */}
          <section className="py-8 relative border-t border-white/5">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                className="bg-white/[0.02] border border-white/5 rounded-xl p-5"
              >
                <div className="flex items-start gap-3">
                  <Shield className="h-5 w-5 text-white/30 flex-shrink-0 mt-0.5" />
                  <p className="text-white/40 text-xs leading-relaxed">
                    <strong className="text-white/50">Important Disclosure:</strong> Information 
                    provided on this page is for informational purposes only and does not constitute 
                    an offer to sell or solicitation of an offer to buy any securities. Investment 
                    opportunities are offered only through official offering documents.
                  </p>
                </div>
              </motion.div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default PressPage;
